import { useCallback, useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { GroupedOptions } from "../../../components/UI/SelectWithSearch"
import { GroupedSelectWithSearch } from "../../../components/UI/SelectWithSearch"
import type { RequirementsDataFile, RequirementsListProps } from "../Interfaces"

export function RequirementsList(props: Readonly<RequirementsListProps>): React.ReactElement {
  const { requirements, setRequirementsFile, requirementsFile, isLoading } = props

  const { t } = useTranslation("scripts", {
    keyPrefix: "customScript.components.requirementsPackages",
  })

  const formatDate = useCallback((date: Date): string => {
    // Format the date
    return date.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "short",
      year: "numeric",
    })
  }, [])

  const getDateString = useCallback(
    (date: string): string => {
      // if the date is not today or yesterday, show the full date
      const now = new Date()
      const today = formatDate(now)
      const yesterday = formatDate(new Date(now.setDate(now.getDate() - 1)))
      if (date === today) {
        return t("requirementsList.today")
      } else if (date === yesterday) {
        return t("requirementsList.yesterday")
      }
      return date
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formatDate],
  )

  // group requirements by day
  const groupedRequirements = useMemo(() => {
    return requirements
      .map((requirement) => {
        const date = new Date(requirement.createdAt as string)
        return {
          ...requirement,
          // if the date is not today or yesterday, show the full date
          date: getDateString(formatDate(date)),
          time: date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true, // Ensures 12-hour format with AM/PM
          }),
        }
      })
      .reduce(
        (acc, requirement) => {
          if (!acc[requirement.date]) {
            acc[requirement.date] = []
          }
          acc[requirement.date]?.push(requirement)
          return acc
        },
        {} as Record<string, Array<RequirementsDataFile & { date: string; time: string }>>,
      )
  }, [requirements, formatDate, getDateString])

  // create map of <uuid, requirement>
  const requirementsMap = useMemo(() => {
    return requirements.reduce(
      (acc, requirement) => {
        acc[`${requirement.index}`] = requirement
        return acc
      },
      {} as Record<string, RequirementsDataFile>,
    )
  }, [requirements])

  const options = useMemo<GroupedOptions>(
    () =>
      Object.keys(groupedRequirements).reduce((acc, key) => {
        acc[key] =
          groupedRequirements[key]?.map((requirement) => ({
            label: requirement.label as string,
            value: `${requirement.index}`,
            subtitle: requirement.time,
            ref: requirement.ref,
          })) ?? []
        return acc
      }, {} as GroupedOptions),
    [groupedRequirements],
  )

  return (
    <GroupedSelectWithSearch
      isOptionsLoading={isLoading}
      options={options}
      initialValue={
        requirementsFile.label === "Choose requirements file"
          ? t("requirementsList.initialValue")
          : requirementsFile.label
      }
      onSelectMenuItem={(item: { label: string; value: string }) =>
        setRequirementsFile(requirementsMap[item.value] as RequirementsDataFile)
      }
      searchInputPlaceHolder={t("requirementsList.searchPlaceholder")}
      hideDescription
      fullWidth={false}
      currentValue={`${requirementsFile.index}`}
    />
  )
}
